import React, { useEffect, useState, useCallback } from 'react';
import { Query, Mutation } from 'react-apollo';
import { withRouter } from 'react-router';
import Swal from 'sweetalert2';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import { withStyles } from '@material-ui/core';
import { compose, get } from '../../utility';
import { withAppUser } from '../../shared/authorization/userConsumer';
import { ROLE_ID } from '../../constants';
import getAllRolesQuery from '../api/getAllRoles.query.graphql';
import createRoleMutation from '../api/createRole.mutation.graphql';
import { UnControlledTable } from '../../shared/table';
import styles from './superAdmin.styles';

const ROLE_CATEGORIES = ['CP_ADMIN', 'ADMIN', 'AGENT'];

const refetchRoles = { query: getAllRolesQuery, variables: { first: 100 } };

function isSuperAdmin(user) {
  if (!user) return false;
  const nodes = get(user, 'systemAclsByUserId.nodes', []);
  return nodes.some(acl => Number(acl.roleId) === ROLE_ID.SUPER_ADMIN);
}

const columns = [
  { Header: 'ID', accessor: 'id', width: 80 },
  { Header: 'Name', accessor: 'roleName' },
  { Header: 'Category', accessor: 'roleCategory' },
  { Header: 'Code', accessor: 'roleCode' },
];

function RolesListC({ classes, user, history }) {
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleCategory, setNewRoleCategory] = useState('ADMIN');
  const [addError, setAddError] = useState('');

  useEffect(() => {
    if (user && !isSuperAdmin(user)) history.replace('/admin');
  }, [user, history]);

  const handleAddRole = useCallback((createRole) => {
    const name = (newRoleName || '').trim();
    if (!name) {
      setAddError('Please enter a role name.');
      return;
    }
    setAddError('');
    createRole({
      variables: {
        role: {
          roleName: name,
          roleCategory: newRoleCategory,
        },
      },
      refetchQueries: [refetchRoles],
    })
      .then(() => {
        setNewRoleName('');
        Swal.fire({
          icon: 'success',
          title: 'Role added successfully',
          toast: true,
          showConfirmButton: false,
          timer: 2000,
        });
      })
      .catch((e) => setAddError(e && e.message ? e.message : 'Failed to add role'));
  }, [newRoleName, newRoleCategory]);

  if (!user || !isSuperAdmin(user)) return null;

  return (
    <div className={classes.root}>
      <Typography className={classes.title} variant="title">Roles (Super Admin)</Typography>

      <div className={classes.addSection}>
        <Typography className={classes.addSectionTitle}>Add role</Typography>
        <Mutation mutation={createRoleMutation}>
          {(createRole) => (
            <div className={classes.addForm}>
              <TextField
                className={classes.addField}
                label="Role name"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="e.g. Custom Admin"
              />
              <FormControl className={classes.addField}>
                <InputLabel>Role category</InputLabel>
                <Select
                  value={newRoleCategory}
                  onChange={(e) => setNewRoleCategory(e.target.value)}
                >
                  {ROLE_CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleAddRole(createRole)}
                className={classes.addButton}
              >
                Add role
              </Button>
              {addError && <Typography color="error" className={classes.addError}>{addError}</Typography>}
            </div>
          )}
        </Mutation>
      </div>

      <Query query={getAllRolesQuery} variables={{ first: 100 }}>
        {({ data, loading, error }) => {
          if (error) return <Typography color="error">Error loading roles.</Typography>;
          const nodes = get(data, 'allRoles.nodes', []);
          return (
            loading ? <Typography color="textSecondary">Loading…</Typography>
              : <UnControlledTable data={nodes} columns={columns} />
          );
        }}
      </Query>
    </div>
  );
}

export default compose(
  withStyles(styles),
  withAppUser,
  withRouter
)(RolesListC);
